import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../UI/Button";
import ComponentCard from "./ComponentCard";
import ComponentFormModal from "./ComponentFormModal";
import PackagingCalculator from "./PackagingCalculator";
import {
  fetchComponents,
  createComponent,
  updateComponent,
  deleteComponent,
} from "./requests";

const emptyComponent = { name: "", quantity_in_product: 1, comment: "" };

export default function ComponentsSection({ productId }) {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setComponents(await fetchComponents(productId));
    } catch (e) {
      setError(e.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing({ ...emptyComponent });
    setFormOpen(true);
  };
  const openEdit = (c) => {
    setEditing({ ...c });
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name || "",
        quantity_in_product: Number(editing.quantity_in_product || 1),
        comment: editing.comment || "",
      };
      if (editing.id) {
        await updateComponent(productId, editing.id, payload);
      } else {
        await createComponent(productId, payload);
      }
      closeForm();
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await deleteComponent(productId, deleting.id);
      setDeleting(null);
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const componentsForCalc = useMemo(
    () => components.map((c) => ({ ...c, packagings: c.packagings || [] })),
    [components]
  );

  return (
    <section className="mt-8 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Составные части и упаковка</h2>
          <p className="text-sm text-gray-500">
            Продукт можно разобрать на части (флакон, крышка, прокладка). У
            каждой части — свои упаковки.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          + Добавить составную часть
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded bg-red-50 border border-red-300 text-red-700 text-sm">
          {error}{" "}
          <button className="underline ml-2" onClick={load}>
            Повторить
          </button>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-center text-gray-500">Загрузка...</div>
      ) : components.length === 0 ? (
        <div className="p-6 border border-dashed rounded text-center text-gray-500">
          Составных частей нет. Добавьте первую — например «флакон», «крышка».
        </div>
      ) : (
        <div className="space-y-4">
          {components.map((c) => (
            <ComponentCard
              key={c.id}
              component={c}
              onEdit={openEdit}
              onDelete={(comp) => setDeleting(comp)}
            />
          ))}
          <PackagingCalculator components={componentsForCalc} />
        </div>
      )}

      {formOpen && editing && (
        <ComponentFormModal
          editing={editing}
          setEditing={setEditing}
          onSave={handleSave}
          onClose={closeForm}
          saving={saving}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-2">
              Удалить составную часть?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              «{deleting.name}» и все её упаковки будут удалены.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setDeleting(null)}
                disabled={saving}
              >
                Отмена
              </Button>
              <Button variant="danger" onClick={confirmDelete} disabled={saving}>
                {saving ? "Удаление..." : "Удалить"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}